import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import api from "../../services/api";
import ConhecimentoCard from "../../components/CardConhecimentoIA";
import ConhecimentoModal from "../../components/ModalCriarConhecimentoIA/ConhecimentoModal";
import { toast } from "react-toastify";
import {
	CONHECIMENTO_CATEGORIES,
	normalizeConhecimentoCategory,
} from "./conhecimentoCategories";

/** A IA no backend só carrega as últimas 50 bases no contexto. */
const MAX_CONHECIMENTOS = 50;

function IconPlus({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconBook({ className = "size-5" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
			<path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
		</svg>
	);
}

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconGrid({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="3" y="3" width="7" height="7" rx="1" />
			<rect x="14" y="3" width="7" height="7" rx="1" />
			<rect x="3" y="14" width="7" height="7" rx="1" />
			<rect x="14" y="14" width="7" height="7" rx="1" />
		</svg>
	);
}

function IconList({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
		</svg>
	);
}

export function ConhecimentoIa() {
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [category, setCategory] = useState("geral");
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [loadingPage, setLoadingPage] = useState(false);
	const [conhecimentos, setConhecimentos] = useState([]);
	const [page, setPage] = useState(1);
	const [open, setOpen] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [openModalEdit, setOpenModalEdit] = useState(false);
	const [selectItem, setSelectItem] = useState();
	const [transferQueues, setTransferQueues] = useState([]);

	const [searchParam, setSearchParam] = useState("");
	const [searchDebounced, setSearchDebounced] = useState("");
	const [sizeFilter, setSizeFilter] = useState("all");
	const [categoryFilter, setCategoryFilter] = useState("all");
	const [sortBy, setSortBy] = useState("recent");
	const [viewMode, setViewMode] = useState("grid");
	const [stats, setStats] = useState({
		total: 0,
		short: 0,
		medium: 0,
		long: 0,
	});
	const [totalCount, setTotalCount] = useState(0);

	const titleHeader = openModalEdit
		? "Editar conhecimento"
		: "Adicionar conhecimento";

	const searchParams = window.location.search.slice(1);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!searchParams) {
				setTransferQueues([]);
				return;
			}
			try {
				const [{ data: agent }, { data: queuesData }] = await Promise.all([
					api.post(`/listarAgente/${searchParams}`),
					api.get("/queue"),
				]);

				let transferIds = [];
				if (Array.isArray(agent?.setoresTransferencia)) {
					transferIds = agent.setoresTransferencia.map(Number);
				} else if (typeof agent?.setoresTransferencia === "string") {
					try {
						const parsed = JSON.parse(agent.setoresTransferencia);
						transferIds = Array.isArray(parsed) ? parsed.map(Number) : [];
					} catch (_) {
						transferIds = [];
					}
				}

				const allQueues = Array.isArray(queuesData) ? queuesData : [];
				const allowed = allQueues.filter((q) =>
					transferIds.some((id) => Number(id) === Number(q.id))
				);
				if (!cancelled) setTransferQueues(allowed);
			} catch (err) {
				console.error(err);
				if (!cancelled) setTransferQueues([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [searchParams]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		const totalAtual = Number(stats.total) || 0;
		if (!openModalEdit && totalAtual >= MAX_CONHECIMENTOS) {
			toast.warn(
				`Limite de ${MAX_CONHECIMENTOS} conhecimentos atingido. A IA usa no máximo as últimas ${MAX_CONHECIMENTOS}. Remova algum para adicionar outro.`
			);
			return;
		}

		setLoadingSubmit(true);

		try {
			const response = await api.post(`/createConhecimento/${searchParams}`, {
				title,
				content,
				category: normalizeConhecimentoCategory(category),
			});

			setConhecimentos((prev) => [response.data, ...prev]);
			setTotalCount((c) => c + 1);
			setStats((prev) => ({
				...prev,
				total: (Number(prev.total) || 0) + 1,
			}));
			setTitle("");
			setContent("");
			setCategory("geral");
			setOpen(false);
			toast.success("Conhecimento adicionado com sucesso!");
		} catch (err) {
			console.error(err);
			toast.error("Erro ao salvar conhecimento.");
		} finally {
			setLoadingSubmit(false);
		}
	};

	useEffect(() => {
		const t = setTimeout(() => setSearchDebounced(searchParam.trim()), 300);
		return () => clearTimeout(t);
	}, [searchParam]);

	const filterKey = [
		searchDebounced,
		sizeFilter,
		categoryFilter,
		sortBy,
		searchParams,
	].join("|");

	const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
	if (filterKey !== prevFilterKey) {
		setPrevFilterKey(filterKey);
		if (page !== 1) setPage(1);
	}

	useEffect(() => {
		if (!searchParams) {
			setConhecimentos([]);
			setHasMore(false);
			setTotalCount(0);
			return undefined;
		}

		let cancelled = false;
		const append = page > 1;

		(async () => {
			setLoadingPage(true);
			try {
				const { data } = await api.post(`/getConhecimento/${searchParams}`, {
					page,
					search: searchDebounced,
					category: categoryFilter,
					size: sizeFilter,
					sortBy,
				});

				if (cancelled) return;

				const payload =
					data && !Array.isArray(data)
						? data
						: { data: Array.isArray(data) ? data : [] };
				const nextData = Array.isArray(payload.data) ? payload.data : [];

				setConhecimentos((prev) => (append ? [...prev, ...nextData] : nextData));
				setHasMore(Boolean(payload.hasMore));
				if (payload.stats) {
					setStats({
						total: Number(payload.stats.total) || 0,
						short: Number(payload.stats.short) || 0,
						medium: Number(payload.stats.medium) || 0,
						long: Number(payload.stats.long) || 0,
					});
				}
				if (payload.count != null) {
					setTotalCount(Number(payload.count) || 0);
				}
			} catch (e) {
				if (cancelled) return;
				console.error(e);
				toast.error("Erro ao carregar conhecimentos.");
			} finally {
				if (!cancelled) setLoadingPage(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		page,
		filterKey,
		searchDebounced,
		sizeFilter,
		categoryFilter,
		sortBy,
		searchParams,
	]);

	const handleEditSave = async (e) => {
		e.preventDefault();

		if (!selectItem) return;

		setLoadingSubmit(true);

		try {
			const { data } = await api.put(`/editConhecimento/${selectItem.id}`, {
				title,
				content,
				category: normalizeConhecimentoCategory(category),
			});
			setConhecimentos((prev) =>
				prev.map((item) =>
					item.id === selectItem.id ? { ...item, ...data } : item
				)
			);

			setOpen(false);
			setOpenModalEdit(false);
			setTitle("");
			setContent("");
			setCategory("geral");
			setSelectItem(null);
			toast.success("Conhecimento atualizado com sucesso!");
		} catch (err) {
			console.error(err);
			toast.error("Erro ao atualizar conhecimento.");
		} finally {
			setLoadingSubmit(false);
		}
	};

	const loadMore = () => {
		if (hasMore && !loadingPage) setPage((prevState) => prevState + 1);
	};

	const handleDeleted = (id) => {
		setConhecimentos((prev) => prev.filter((item) => item.id !== id));
		setStats((prev) => ({
			...prev,
			total: Math.max(0, (Number(prev.total) || 0) - 1),
		}));
		setTotalCount((c) => Math.max(0, c - 1));
	};

	const handleUpdated = (id, updatedData) => {
		setConhecimentos((prev) =>
			prev.map((item) =>
				item.id === id ? { ...item, ...updatedData } : item
			)
		);
	};

	const hasActiveFilters =
		Boolean(searchParam.trim()) ||
		sizeFilter !== "all" ||
		categoryFilter !== "all" ||
		sortBy !== "recent";

	const clearFilters = () => {
		setSearchParam("");
		setSearchDebounced("");
		setSizeFilter("all");
		setCategoryFilter("all");
		setSortBy("recent");
	};

	const openCreate = () => {
		const totalAtual = Number(stats.total) || 0;
		if (totalAtual >= MAX_CONHECIMENTOS) {
			toast.warn(
				`Limite de ${MAX_CONHECIMENTOS} conhecimentos atingido. A IA usa no máximo as últimas ${MAX_CONHECIMENTOS}. Remova algum para adicionar outro.`
			);
			return;
		}
		setOpenModalEdit(false);
		setSelectItem(null);
		setTitle("");
		setContent("");
		setCategory("geral");
		setOpen(true);
	};

	const totalConhecimentos = Number(stats.total) || 0;
	const atLimit = totalConhecimentos >= MAX_CONHECIMENTOS;

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<ConhecimentoModal
						titleHeader={titleHeader}
						open={open}
						handleClose={() => {
							setOpen(false);
							setOpenModalEdit(false);
						}}
						handleSubmit={openModalEdit ? handleEditSave : handleSubmit}
						title={title}
						setTitle={setTitle}
						content={content}
						setContent={setContent}
						category={category}
						setCategory={setCategory}
						loading={loadingSubmit}
						transferQueues={transferQueues}
					/>

					<AppPageTitle title="Conhecimentos" subtitle="Agentes IA" />

					<div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
						<div className="max-w-2xl">
							<p className="m-0 text-sm leading-6 text-default-500 dark:text-default-400">
								Gerencie a base de conhecimento usada pelo agente. Títulos claros e
								conteúdos objetivos ajudam a IA a encontrar o contexto certo.
							</p>
						</div>
						<div className="flex flex-col gap-3 sm:flex-row">
							<Link
								to="/AgenteIa"
								className="btn btn-sm border border-default-200 bg-transparent text-default-600 no-underline hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
							>
								Voltar
							</Link>
							<button
								type="button"
								onClick={openCreate}
								disabled={atLimit}
								title={
									atLimit
										? `Limite de ${MAX_CONHECIMENTOS} conhecimentos atingido`
										: undefined
								}
								className="btn btn-sm bg-primary text-white disabled:cursor-not-allowed disabled:opacity-60"
							>
								<IconPlus className="size-4" />
								Adicionar conhecimento
							</button>
						</div>
					</div>

					<div
						className={`mb-5 rounded-2xl border px-4 py-3 text-sm leading-6 ${
							atLimit
								? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100"
								: "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-100"
						}`}
						role="status"
					>
						<p className="m-0 font-semibold">
							Limite da base: {totalConhecimentos}/{MAX_CONHECIMENTOS}
						</p>
						<p className="mt-1 mb-0 opacity-90">
							{atLimit
								? `Você já tem ${MAX_CONHECIMENTOS} ou mais conhecimentos. Não é possível adicionar novos — a IA usa no máximo as últimas ${MAX_CONHECIMENTOS}. Remova algum item para liberar espaço.`
								: `A IA carrega no máximo as últimas ${MAX_CONHECIMENTOS} bases de conhecimento no contexto. Cadastre com critério para manter as informações mais importantes.`}
						</p>
					</div>

					<div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
						<div className="rounded-2xl border border-default-200 bg-white px-4 py-3 dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-400">
								Total
							</p>
							<p className="mt-1 mb-0 text-2xl font-semibold tabular-nums text-default-900 dark:text-default-50">
								{stats.total}
								<span className="ms-1 text-sm font-medium text-default-400">
									/ {MAX_CONHECIMENTOS}
								</span>
							</p>
						</div>
						<div className="rounded-2xl border border-default-200 bg-white px-4 py-3 dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-400">
								Curtos
							</p>
							<p className="mt-1 mb-0 text-2xl font-semibold tabular-nums text-default-900 dark:text-default-50">
								{stats.short}
							</p>
						</div>
						<div className="rounded-2xl border border-default-200 bg-white px-4 py-3 dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-400">
								Médios
							</p>
							<p className="mt-1 mb-0 text-2xl font-semibold tabular-nums text-default-900 dark:text-default-50">
								{stats.medium}
							</p>
						</div>
						<div className="rounded-2xl border border-default-200 bg-white px-4 py-3 dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-400">
								Longos
							</p>
							<p className="mt-1 mb-0 text-2xl font-semibold tabular-nums text-default-900 dark:text-default-50">
								{stats.long}
							</p>
						</div>
					</div>

					<div className="card mb-5">
						<div className="card-body space-y-4">
							<div className="flex items-start gap-3">
								<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
									<IconBook className="size-5" />
								</span>
								<div className="min-w-0 flex-1">
									<h6 className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
										Filtros e visualização
									</h6>
									<p className="mt-1 mb-0 text-sm leading-6 text-default-600 dark:text-default-300">
										Busque por título ou conteúdo, filtre pelo tamanho e escolha como
										visualizar a base.
									</p>
								</div>
							</div>

							<div className="flex flex-col gap-3 lg:flex-row lg:items-end">
								<div className="min-w-0 flex-1">
									<label className="mb-1.5 block text-xs font-medium text-default-500">
										Buscar
									</label>
									<div className="relative">
										<input
											type="search"
											className="form-input form-input-sm w-full ps-11"
											placeholder="Buscar por título ou conteúdo..."
											value={searchParam}
											onChange={(e) => setSearchParam(e.target.value)}
										/>
										<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
											<IconSearch />
										</div>
									</div>
								</div>

								<div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:w-[36rem]">
									<label className="block">
										<span className="mb-1.5 block text-xs font-medium text-default-500">
											Categoria
										</span>
										<select
											className="form-input form-input-sm w-full"
											value={categoryFilter}
											onChange={(e) => setCategoryFilter(e.target.value)}
										>
											<option value="all">Todas</option>
											{CONHECIMENTO_CATEGORIES.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
									</label>
									<label className="block">
										<span className="mb-1.5 block text-xs font-medium text-default-500">
											Tamanho
										</span>
										<select
											className="form-input form-input-sm w-full"
											value={sizeFilter}
											onChange={(e) => setSizeFilter(e.target.value)}
										>
											<option value="all">Todos</option>
											<option value="short">Curtos (&lt; 200)</option>
											<option value="medium">Médios (200–800)</option>
											<option value="long">Longos (&gt; 800)</option>
										</select>
									</label>
									<label className="block">
										<span className="mb-1.5 block text-xs font-medium text-default-500">
											Ordenar
										</span>
										<select
											className="form-input form-input-sm w-full"
											value={sortBy}
											onChange={(e) => setSortBy(e.target.value)}
										>
											<option value="recent">Mais recentes</option>
											<option value="oldest">Mais antigos</option>
											<option value="category">Por categoria</option>
											<option value="titleAsc">Título A–Z</option>
											<option value="titleDesc">Título Z–A</option>
											<option value="longest">Conteúdo maior</option>
											<option value="shortest">Conteúdo menor</option>
										</select>
									</label>
								</div>

								<div className="flex items-center gap-2">
									<div className="inline-flex rounded-xl border border-default-200 p-1 dark:border-default-700">
										<button
											type="button"
											title="Grade"
											onClick={() => setViewMode("grid")}
											className={`inline-flex size-8 items-center justify-center rounded-lg transition ${
												viewMode === "grid"
													? "bg-primary text-white"
													: "text-default-500 hover:bg-default-100 dark:hover:bg-default-800"
											}`}
										>
											<IconGrid />
										</button>
										<button
											type="button"
											title="Lista"
											onClick={() => setViewMode("list")}
											className={`inline-flex size-8 items-center justify-center rounded-lg transition ${
												viewMode === "list"
													? "bg-primary text-white"
													: "text-default-500 hover:bg-default-100 dark:hover:bg-default-800"
											}`}
										>
											<IconList />
										</button>
									</div>
									{hasActiveFilters && (
										<button
											type="button"
											onClick={clearFilters}
											className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
										>
											Limpar
										</button>
									)}
								</div>
							</div>
						</div>
					</div>

					{loadingPage && conhecimentos.length === 0 ? (
						<div className="app-modal-loading py-16">
							<div className="app-modal-spinner" />
						</div>
					) : conhecimentos.length === 0 ? (
						<div className="card">
							<div className="card-body flex flex-col items-center justify-center gap-3 py-16 text-center">
								{hasActiveFilters ? (
									<>
										<p className="m-0 font-semibold text-default-800 dark:text-default-100">
											Nenhum resultado para os filtros
										</p>
										<p className="m-0 text-sm text-default-500 dark:text-default-400">
											Ajuste a busca ou limpe os filtros para ver todos os conhecimentos.
										</p>
										<button
											type="button"
											onClick={clearFilters}
											className="btn btn-sm mt-2 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
										>
											Limpar filtros
										</button>
									</>
								) : (
									<>
										<span className="inline-flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
											<IconBook className="size-6" />
										</span>
										<p className="m-0 font-semibold text-default-800 dark:text-default-100">
											Nenhum conhecimento cadastrado
										</p>
										<p className="m-0 max-w-md text-sm text-default-500 dark:text-default-400">
											Adicione o primeiro conhecimento para enriquecer as respostas do agente.
											Limite: {MAX_CONHECIMENTOS} itens (a IA usa no máximo as últimas {MAX_CONHECIMENTOS}).
										</p>
										<button
											type="button"
											onClick={openCreate}
											disabled={atLimit}
											className="btn btn-sm mt-1 bg-primary text-white disabled:cursor-not-allowed disabled:opacity-60"
										>
											<IconPlus className="size-4" />
											Adicionar conhecimento
										</button>
									</>
								)}
							</div>
						</div>
					) : (
						viewMode === "grid" ? (
							<div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
								{conhecimentos.map((item) => (
									<ConhecimentoCard
										key={item.id}
										item={item}
										viewMode={viewMode}
										onDeleted={handleDeleted}
										onUpdated={handleUpdated}
										selectItem={setSelectItem}
										openModalEdit={setOpenModalEdit}
										setTitle={setTitle}
										setContent={setContent}
										setCategory={setCategory}
										setOpen={setOpen}
									/>
								))}
							</div>
						) : (
							<div className="overflow-hidden rounded-2xl border border-default-200 bg-white dark:border-default-700 dark:bg-default-900">
								<div className="hidden border-b border-default-200 bg-default-50/90 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-default-400 sm:grid sm:grid-cols-[1fr_auto] dark:border-default-800 dark:bg-default-950/50">
									<span>Conhecimento</span>
									<span className="pr-1 text-right">Ações</span>
								</div>
								{conhecimentos.map((item) => (
									<ConhecimentoCard
										key={item.id}
										item={item}
										viewMode={viewMode}
										onDeleted={handleDeleted}
										onUpdated={handleUpdated}
										selectItem={setSelectItem}
										openModalEdit={setOpenModalEdit}
										setTitle={setTitle}
										setContent={setContent}
										setCategory={setCategory}
										setOpen={setOpen}
									/>
								))}
							</div>
						)
					)}

					{conhecimentos.length > 0 && (
						<div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-default-500 md:justify-between">
							<p className="m-0 text-sm text-default-500 dark:text-default-400">
								Exibindo <b>{conhecimentos.length}</b> de{" "}
								<b>{totalCount || stats.total}</b> conhecimento(s)
							</p>
							{hasMore && (
								<button
									type="button"
									onClick={loadMore}
									disabled={loadingPage}
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
								>
									{loadingPage ? "Carregando..." : "Carregar mais"}
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		</MainContainer>
	);
}
